<template>
  <div v-if="isVisible" class="base-alert__container" :class="`base-alert__${type}`">
    <div class="base-alert__left">
      <!-- Icon -->
      <div class="base-alert__icon">
        <KIcon :icon="`fa-solid ${iconMap[type]}`" :size="iconSize" />
      </div>

      <!-- Message -->
      <div class="base-alert__message">
        <p v-if="showPrefix" class="base-alert__prefix">
          {{ prefix }}
        </p>
        <p v-if="message" v-html="message" class="base-alert__suffix" />
        <p v-else><slot name="message" /></p>
      </div>
    </div>

    <!-- Button -->
    <KButton
      v-if="dismissible"
      id="dismiss-alert-button"
      class="base-alert__button"
      aria-label="Dismiss alert"
      text
      @click="isVisible = false"
    >
      <template #icon>
        <KIcon icon="fa-solid fa-xmark" />
      </template>
    </KButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { KIcon, KButton } from "@k2united/frontend_components";
import { useResponsive } from "@/composables/useResponsive";

interface Props {
  message?: string;
  type?: "error" | "warning" | "info" | "caution" | "success";
  size?:
    | "2xs"
    | "xs"
    | "sm"
    | "lg"
    | "xl"
    | "2xl"
    | "1x"
    | "2x"
    | "3x"
    | "4x"
    | "5x"
    | "6x"
    | "7x"
    | "8x"
    | "9x"
    | "10x";
  prefix?: string;
  hidePrefix?: boolean;
  dismissible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  message: "",
  type: "error",
  size: undefined,
  prefix: undefined,
  hidePrefix: false,
  dismissible: false
});

const { isMobile } = useResponsive();

const isVisible = ref(true);

// Icon
const iconMap = {
  error: "fa-square-exclamation",
  warning: "fa-do-not-enter",
  info: "fa-circle-info",
  caution: "fa-triangle-exclamation",
  success: "fa-circle-check"
};

const iconSize = computed(() => {
  if (props.size) {
    return props.size;
  }

  return isMobile.value ? "1x" : "lg";
});

// Prefix
const prefix = computed(() => {
  if (props.prefix) {
    return props.prefix;
  }

  if (props.type === "error") {
    return "Whoops!";
  }

  return "WARNING:";
});

const showPrefix = computed(() => {
  if (props.hidePrefix) {
    return false;
  }

  return props.prefix || props.type === "error" || props.type === "warning";
});
</script>

<style scoped lang="scss">
.base-alert__container {
  align-items: flex-start;
  border: 1px solid transparent;
  border-radius: 5px;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.5rem;

  @include tablet {
    padding: 0.75rem;
  }

  .base-alert__left {
    align-items: flex-start;
    display: flex;
    gap: 0.5rem;
  }

  .base-alert__icon {
    align-items: center;
    display: flex;
    justify-content: center;
    padding-top: 0.0625rem;
  }

  .base-alert__message {
    @include body-small;
    color: $color-text-default;

    @include tablet {
      @include body;
    }
  }

  :deep(.base-alert__message a) {
    @include link-small;

    @include tablet {
      @include link;
    }
  }

  .base-alert__prefix {
    @include body-small-bold;

    @include tablet {
      @include body-bold;
    }
  }

  .base-alert__button {
    background: rgba(62, 62, 62, 0.03);
    height: 1.5rem;
    width: 1.5rem;

    &:hover,
    &:focus {
      background: $color-standard-neutral-neutral200;
    }
  }
}

:deep(.base-alert__suffix a) {
  @include link-semi-bold;
}

.base-alert {
  &__error {
    background: $color-background-danger;
    border-color: $color-border-danger;

    .base-alert__icon {
      color: $color-icon-danger;
    }
  }

  &__warning {
    background: $color-background-danger;
    border-color: $color-border-danger;

    .base-alert__icon {
      color: $color-icon-danger;
    }
  }

  &__info {
    background: $color-background-info;
    border-color: $color-border-info;

    .base-alert__icon {
      color: $color-icon-info;
    }
  }

  &__caution {
    background: $color-background-warning;
    border-color: $color-border-warning;

    .base-alert__icon {
      color: $color-icon-warning;
    }
  }

  &__success {
    background: $color-background-success;
    border-color: $color-border-success;

    .base-alert__icon {
      color: $color-icon-success;
    }
  }
}
</style>
